import { useTranslation } from 'react-i18next';
import { Badge, Icon, IconButton, ProgressBar } from '../../ui/index.ts';
import { alarmPercent, clock } from '../format.ts';
import { useGame } from '../GameContext.tsx';
import styles from './HudBar.module.css';

export interface HudBarProps {
  onLeave: () => void;
  onPause: () => void;
}

/** Phase, clock, alarm and role: the strip that stays on screen through the whole heist. */
export function HudBar({ onLeave, onPause }: HudBarProps) {
  const { t } = useTranslation('game');
  const { view, client, g } = useGame();
  const exec = view.exec;
  const now = view.game.clockMs;

  const phaseClock =
    view.briefing && view.game.phase === 'briefing'
      ? view.briefing.deadlineMs - now
      : view.planning && view.game.phase === 'planning'
        ? view.planning.deadlineMs - now
        : view.hideout
          ? view.hideout.deadlineMs - now
          : null;
  const low = exec !== null && exec.timerMs < 60_000;

  return (
    <header className={styles['hud']}>
      <IconButton icon="back" label={t('hud.leave')} onClick={onLeave} />
      <div className={styles['phase']}>
        <span className={styles['phaseName']}>{g(`phase.${view.game.phase}`)}</span>
        {phaseClock !== null ? (
          <span className={styles['phaseClock']}>
            <Icon name="clock" size="1em" /> {clock(phaseClock)}
          </span>
        ) : null}
        <Badge tone="cyan">{g(`roles.${view.you.role}`)}</Badge>
      </div>
      {exec ? (
        <div className={styles['gauges']}>
          <div className={[styles['timer'], low ? styles['low'] : ''].join(' ')}>
            <span className="visually-hidden">{t('hud.timer')}</span>
            <span role="timer">{clock(exec.timerMs)}</span>
            {exec.ratePermille > 1000 ? (
              <Badge tone="pink">{t('hud.faster', { pct: exec.ratePermille / 10 })}</Badge>
            ) : null}
          </div>
          <div className={styles['alarm']}>
            <ProgressBar
              value={alarmPercent(exec.alarm)}
              label={t('hud.alarm')}
              tone="alarm"
              showValue
            />
          </div>
        </div>
      ) : (
        <div className={styles['gauges']} />
      )}
      {client.canPause ? (
        <div className={styles['pause']}>
          <IconButton
            icon="pause"
            label={client.paused() ? t('hud.resume') : t('hud.pause')}
            onClick={onPause}
          />
        </div>
      ) : null}
    </header>
  );
}
